import React, { useState, useCallback, useRef, useMemo } from 'react';
import './GraphVisualization.css';

// =========================================
// Constants
// =========================================

const PADDING = 60;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_SENSITIVITY = 0.001;
const CLICK_THRESHOLD = 5; // px — max mouse movement to count as click, not drag
const NEBULA_EXPANSION = 30;  // SVG units — hull expansion beyond node positions
const NEBULA_OPACITY = 0.12;  // Soft glow — slightly higher for dark background visibility

// Cited/uncited (one-term and 3+ term fallback)
const COLOR_CITED = '#FFFFFF';           // Bright star
const COLOR_UNCITED = '#5A6B7A';         // Dim star

// Multi-term coloring (2-3 terms)
const COLOR_TERM_A_CITED = '#E8944A';    // Warm orange (Webb infrared)
const COLOR_TERM_A_UNCITED = '#8B6540';  // Dim orange
const COLOR_TERM_B_CITED = '#FFFFFF';    // Bright white (Hubble starlight)
const COLOR_TERM_B_UNCITED = '#7B8FA0';  // Muted blue
const COLOR_TERM_C_CITED = '#5EC4D4';    // Bright teal
const COLOR_TERM_C_UNCITED = '#3A7A84';  // Dim teal
const COLOR_BLEND_CITED = '#F0C060';     // Bright gold (matches 2+ terms)
const COLOR_BLEND_UNCITED = '#8B7040';   // Dim gold
const COLOR_NEITHER = '#5A6B7A';         // Same as uncited

// Edges
const COLOR_EDGE = '#5A7080';            // Lighter blue-gray, clearly visible on dark

// Stroke
const COLOR_STROKE = '#FFFFFF';          // White outline for cited (bright stars need definition)
const COLOR_STROKE_UNCITED = '#5A7080';  // Matches edge color — consistent visual language

// Nebula
const NEBULA_COLOR = '#7B9BD4';          // Cornflower/periwinkle blue

// =========================================
// Star Icon Definitions (Noun Project SVGs)
// =========================================

// Small star (noun-star-1187057): 4-point soft starburst
// Original viewBox: 0 0 1200 1200, center: (600, 600)
const STAR_SMALL = {
  path: 'm599.88 83.879c-0.027343 0-0.054687 0.003906-0.085937 0.003906-0.10547 0.007813-0.21484 0.015626-0.32031 0.023438-0.30859 0.011719-0.61719 0.035156-0.92578 0.074219-5.5547 0.66406-9.9141 5.0859-10.5 10.652-53.219 273.23-205.01 445.75-493.25 493.45-5.207 0.45703-9.5195 4.2266-10.664 9.3242-0.007812 0.039062-0.015624 0.082031-0.023437 0.12109-0.074219 0.33984-0.13281 0.68359-0.17578 1.0312-0.011719 0.09375-0.019532 0.18359-0.027344 0.27734-0.03125 0.27344-0.050781 0.55078-0.058594 0.82812-0.007812 0.18359-0.007812 0.36719 0 0.55078 0 0.19531 0.003906 0.39062 0.015625 0.58984 0.015625 0.27344 0.046875 0.55078 0.085938 0.82422 0.011719 0.09375 0.023437 0.1875 0.035156 0.27734 0.058594 0.37109 0.13281 0.73828 0.22656 1.1055 1.2578 4.9844 5.5586 8.6172 10.688 9.0234 288.13 47.723 439.88 220.17 493.12 493.29 0.53516 5.7305 5.0625 10.273 10.793 10.828h0.10156 0.019532c0.23438 0.007812 0.46484 0.007812 0.69531 0 0.19141 0.007812 0.38281 0.007812 0.57422 0 0.082031 0.003906 0.16797 0.003906 0.25 0 5.9688-0.21484 10.867-4.7852 11.5-10.723 53.336-273.23 205-445.74 493.22-493.45v-0.003906c5.2188-0.44141 9.5469-4.2148 10.695-9.3242 0.007812-0.042969 0.015624-0.082032 0.023437-0.12109 0.074219-0.33984 0.13281-0.6875 0.17578-1.0312 0.011719-0.09375 0.019532-0.18359 0.027344-0.27734 0.03125-0.27344 0.050781-0.55078 0.058594-0.82812 0.007812-0.18359 0.007812-0.36719 0-0.55078 0-0.19531-0.003906-0.39453-0.011719-0.58984-0.019531-0.27734-0.050781-0.55078-0.085937-0.82812-0.011719-0.089844-0.023438-0.18359-0.039063-0.27344-0.058594-0.37109-0.13281-0.74219-0.22266-1.1055-1.2578-4.9805-5.5469-8.6055-10.664-9.0234-288.14-47.715-439.79-220.16-493.15-493.29-0.53516-5.7305-5.0625-10.273-10.793-10.828-0.035156-0.003906-0.066406-0.007813-0.10156-0.011719h-0.027344c-0.23047-0.011719-0.46094-0.019531-0.69141-0.023437-0.16016-0.011719-0.32031-0.019532-0.48438-0.023438z',
  cx: 600, cy: 600, size: 1200,
  targetSize: 14,
  collisionRadius: 7,
  fillRule: 'evenodd',
};

// Medium star (noun-star-23182): 4-point sharp star
// Original viewBox: 0 0 100 100, center: (50, 50)
const STAR_MEDIUM = {
  path: 'M38.548,46.201c3.831-0.378,7.275-3.821,7.653-7.653l3.112-31.582c0.378-3.831,0.995-3.831,1.373,0l3.112,31.582c0.378,3.831,3.821,7.275,7.653,7.653l31.582,3.112c3.831,0.378,3.831,0.995,0,1.373l-31.582,3.112c-3.831,0.378-7.275,3.821-7.653,7.653l-3.112,31.582c-0.378,3.831-0.995,3.831-1.373,0l-3.112-31.582c-0.378-3.831-3.821-7.275-7.653-7.653L6.966,50.686c-3.831-0.378-3.831-0.995,0-1.373L38.548,46.201z',
  cx: 50, cy: 50, size: 100,
  targetSize: 32,
  collisionRadius: 16,
};

// Large star (noun-star-187429): 8-point asterisk star
// Original viewBox: 0 0 100 100, center: (50, 50)
const STAR_LARGE = {
  path: 'M91.25,50 L54.903,47.234 L67.887,32.114 L52.765,45.096 L50,8.75 L47.234,45.096 L32.113,32.114 L45.097,47.234 L8.75,50 L45.096,52.765 L32.114,67.886 L47.234,54.903 L50,91.25 L52.765,54.902 L67.886,67.886 L54.903,52.765Z',
  cx: 50, cy: 50, size: 100,
  targetSize: 48,
  collisionRadius: 24,
};

// =========================================
// Helper Functions
// =========================================

/**
 * Get the star tier definition based on entryCount.
 * Small (1-2), Medium (3-9), Large (10+).
 */
const getStarTier = (node) => {
  if (node.entryCount >= 10) return STAR_LARGE;
  if (node.entryCount >= 3) return STAR_MEDIUM;
  return STAR_SMALL;
};

/**
 * Compute node collision radius based on star tier.
 */
const nodeRadius = (node) => getStarTier(node).collisionRadius;

/**
 * Check if a node's tags match a search term (case-insensitive substring).
 */
const nodeMatchesTerm = (node, term) => {
  const lower = term.toLowerCase();
  return node.tags.some(tag => tag.toLowerCase().includes(lower));
};

/**
 * Determine node fill color based on concept tags and cited status.
 * 1 tag or 4+: simple cited/uncited.
 * 2-3 tags: per-tag colors with gold blend for multi-match nodes.
 */
const getNodeColor = (node, conceptTags) => {
  if (!conceptTags || conceptTags.length === 0 || conceptTags.length === 1 || conceptTags.length >= 4) {
    return node.cited ? COLOR_CITED : COLOR_UNCITED;
  }

  // 2-3 term coloring
  const matches = conceptTags.map(term => nodeMatchesTerm(node, term));
  const matchCount = matches.filter(Boolean).length;

  // Blend: matches 2+ terms (any combination)
  if (matchCount >= 2) return node.cited ? COLOR_BLEND_CITED : COLOR_BLEND_UNCITED;

  // Single-term matches
  if (matches[0]) return node.cited ? COLOR_TERM_A_CITED : COLOR_TERM_A_UNCITED;
  if (matches[1]) return node.cited ? COLOR_TERM_B_CITED : COLOR_TERM_B_UNCITED;
  if (matches[2]) return node.cited ? COLOR_TERM_C_CITED : COLOR_TERM_C_UNCITED;

  return COLOR_NEITHER;
};

/**
 * Get node stroke color — cited nodes get dark stroke, uncited get secondary stroke.
 */
const getNodeStroke = (node) => node.cited ? COLOR_STROKE : COLOR_STROKE_UNCITED;

/**
 * Compute initial viewBox from node positions.
 * Uses 5th-95th percentile for sets of 10+ nodes to handle outliers.
 */
const computeBounds = (nodes) => {
  if (!nodes || nodes.length === 0) {
    return { x: 0, y: 0, w: 1000, h: 1000 };
  }

  const n = nodes.length;

  // Small sets: use all nodes (percentile clipping doesn't help)
  if (n < 10) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const node of nodes) {
      const r = nodeRadius(node);
      if (node.x - r < minX) minX = node.x - r;
      if (node.y - r < minY) minY = node.y - r;
      if (node.x + r > maxX) maxX = node.x + r;
      if (node.y + r > maxY) maxY = node.y + r;
    }
    return {
      x: minX - PADDING,
      y: minY - PADDING,
      w: (maxX - minX) + PADDING * 2,
      h: (maxY - minY) + PADDING * 2
    };
  }

  // Larger sets: 5th-95th percentile to clip outliers
  const xs = nodes.map(nd => nd.x).sort((a, b) => a - b);
  const ys = nodes.map(nd => nd.y).sort((a, b) => a - b);

  const lo = Math.floor(n * 0.05);
  const hi = Math.floor(n * 0.95);

  const minX = xs[lo];
  const maxX = xs[hi];
  const minY = ys[lo];
  const maxY = ys[hi];

  return {
    x: minX - PADDING,
    y: minY - PADDING,
    w: (maxX - minX) + PADDING * 2,
    h: (maxY - minY) + PADDING * 2
  };
};

/**
 * Map edge weight to stroke width (1.0–4.0px range).
 */
const edgeStrokeWidth = (weight) => 1.0 + weight * 3.0;

/**
 * Map edge weight to opacity (0.25–0.60 range).
 */
const edgeOpacity = (weight) => 0.25 + weight * 0.35;

// =========================================
// Convex Hull Helpers (Nebula Overlay)
// =========================================

/**
 * Compute convex hull using Jarvis march (gift wrapping).
 * Returns hull points in winding order.
 */
const convexHull = (points) => {
  const n = points.length;
  if (n < 3) return [...points];

  // Find leftmost point (guaranteed on hull)
  let l = 0;
  for (let i = 1; i < n; i++) {
    if (points[i].x < points[l].x) l = i;
  }

  const hull = [];
  let p = l;
  do {
    hull.push(points[p]);
    let q = (p + 1) % n;
    for (let i = 0; i < n; i++) {
      const cross = (points[q].x - points[p].x) * (points[i].y - points[p].y)
                  - (points[q].y - points[p].y) * (points[i].x - points[p].x);
      if (cross < 0) q = i;
    }
    p = q;
  } while (p !== l && hull.length <= n);

  return hull;
};

/**
 * Expand hull points outward from centroid by a fixed distance.
 */
const expandHull = (hull, amount) => {
  let cx = 0, cy = 0;
  for (const p of hull) { cx += p.x; cy += p.y; }
  cx /= hull.length;
  cy /= hull.length;

  return hull.map(p => {
    const dx = p.x - cx;
    const dy = p.y - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) return { ...p };
    return {
      x: p.x + (dx / dist) * amount,
      y: p.y + (dy / dist) * amount
    };
  });
};

/**
 * Convert hull points to a smooth closed SVG path using quadratic bezier curves.
 * Curves pass through edge midpoints; hull vertices serve as control points.
 */
const smoothHullPath = (hull) => {
  const n = hull.length;
  if (n < 3) return '';

  const mids = hull.map((p, i) => {
    const next = hull[(i + 1) % n];
    return { x: (p.x + next.x) / 2, y: (p.y + next.y) / 2 };
  });

  let d = `M${mids[0].x},${mids[0].y}`;
  for (let i = 0; i < n; i++) {
    const cp = hull[(i + 1) % n];
    const end = mids[(i + 1) % n];
    d += ` Q${cp.x},${cp.y} ${end.x},${end.y}`;
  }
  d += 'Z';
  return d;
};

// =========================================
// Component
// =========================================

const GraphVisualization = ({ graphData, conceptTags }) => {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef(null);
  const clickOrigin = useRef(null);

  const nodes = graphData?.nodes;
  const edges = graphData?.edges;

  const initialBounds = useMemo(
    () => computeBounds(nodes),
    [nodes]
  );

  const [viewBox, setViewBox] = useState(null);
  const currentVB = viewBox || initialBounds;

  // Reset viewBox when new graph data arrives
  const prevNodesRef = useRef(nodes);
  if (nodes !== prevNodesRef.current) {
    prevNodesRef.current = nodes;
    if (viewBox !== null) setViewBox(null);
  }

  // =========================================
  // Nebula Overlay (blend node hull for 2-3 term queries)
  // =========================================

  const nebulaPath = useMemo(() => {
    if (!conceptTags || conceptTags.length < 2 || conceptTags.length >= 4 || !nodes || nodes.length === 0) return null;

    // Find blend nodes — match 2 or more search terms
    const blendNodes = nodes.filter(node => {
      const matchCount = conceptTags.filter(term => nodeMatchesTerm(node, term)).length;
      return matchCount >= 2;
    });
    if (blendNodes.length < 3) return null;

    const points = blendNodes.map(n => ({ x: n.x, y: n.y }));
    const hull = convexHull(points);
    if (hull.length < 3) return null;

    const expanded = expandHull(hull, NEBULA_EXPANSION);
    return smoothHullPath(expanded);
  }, [nodes, conceptTags]);

  // =========================================
  // Event Handlers
  // =========================================

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const vb = viewBox || initialBounds;

    // Cursor position as fraction of SVG element
    const fracX = (e.clientX - rect.left) / rect.width;
    const fracY = (e.clientY - rect.top) / rect.height;

    // Cursor position in viewBox coordinates
    const cursorX = vb.x + fracX * vb.w;
    const cursorY = vb.y + fracY * vb.h;

    // Zoom factor
    const delta = -e.deltaY * ZOOM_SENSITIVITY;
    const scale = Math.exp(delta);

    const newW = vb.w / scale;
    const newH = vb.h / scale;

    // Clamp zoom
    const zoomLevel = initialBounds.w / newW;
    if (zoomLevel < ZOOM_MIN || zoomLevel > ZOOM_MAX) return;

    // Keep cursor at same viewBox position
    setViewBox({
      x: cursorX - fracX * newW,
      y: cursorY - fracY * newH,
      w: newW,
      h: newH
    });
  }, [viewBox, initialBounds]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return; // left click only
    const vb = viewBox || initialBounds;
    panStart.current = { clientX: e.clientX, clientY: e.clientY, vb };
    clickOrigin.current = { x: e.clientX, y: e.clientY };
    setIsPanning(true);
  }, [viewBox, initialBounds]);

  const handleMouseMove = useCallback((e) => {
    // Tooltip tracking
    if (!isPanning) {
      // Tooltip position updates are handled by individual element handlers
      return;
    }

    // Panning
    if (!panStart.current) return;
    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const { clientX, clientY, vb } = panStart.current;

    const dx = ((e.clientX - clientX) / rect.width) * vb.w;
    const dy = ((e.clientY - clientY) / rect.height) * vb.h;

    setViewBox({
      x: vb.x - dx,
      y: vb.y - dy,
      w: vb.w,
      h: vb.h
    });
  }, [isPanning]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    panStart.current = null;
  }, []);

  const handleResetView = useCallback(() => {
    setViewBox(null);
  }, []);

  // =========================================
  // Node Click Handler
  // =========================================

  const handleNodeClick = useCallback((e, node) => {
    // Journal entries don't have external URLs
    if (node.id.startsWith('journal:')) return;
    // Only fire if mouse hasn't moved (click, not drag)
    if (!clickOrigin.current) return;
    const dx = e.clientX - clickOrigin.current.x;
    const dy = e.clientY - clickOrigin.current.y;
    if (Math.sqrt(dx * dx + dy * dy) < CLICK_THRESHOLD) {
      window.open(node.id, '_blank', 'noopener,noreferrer');
    }
  }, []);

  // =========================================
  // Tooltip Handlers
  // =========================================

  const handleNodeHover = useCallback((e, node) => {
    setTooltip({
      x: e.clientX,
      y: e.clientY,
      content: (
        <div>
          <div className="gv-tooltip-title">{node.docTitle}</div>
          <div className="gv-tooltip-counts">
            {node.isJournal ? 'Journal' : `F: ${node.focusCount}  T: ${node.thoughtCount}`}
          </div>
          <div className="gv-tooltip-status">
            {node.cited ? 'Cited in response' : 'Analyzed, not cited'}
          </div>
        </div>
      )
    });
  }, []);

  const handleEdgeHover = useCallback((e, edge) => {
    const pct = Math.round(edge.weight * 100);
    setTooltip({
      x: e.clientX,
      y: e.clientY,
      content: (
        <div>
          <div className="gv-tooltip-title">Connection ({pct}%)</div>
          <div className="gv-tooltip-tags">
            {edge.sharedTags.join(', ')}
          </div>
        </div>
      )
    });
  }, []);

  const handleTooltipMove = useCallback((e) => {
    if (tooltip) {
      setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
    }
  }, [tooltip]);

  const handleMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  // =========================================
  // Legend
  // =========================================

  const renderLegend = () => {
    const termCount = conceptTags ? conceptTags.length : 0;
    const showTermColors = termCount === 2 || termCount === 3;

    return (
      <div className="gv-legend">
        {/* Star sizes */}
        <div className="gv-legend-section">
          <div className="gv-legend-row">
            <span className="gv-legend-icon">
              <svg width="12" height="12" viewBox="0 0 1200 1200">
                <path d={STAR_SMALL.path} fill={COLOR_CITED} fillRule="evenodd" />
              </svg>
            </span>
            <span>1-2 entries</span>
          </div>
          <div className="gv-legend-row">
            <span className="gv-legend-icon">
              <svg width="18" height="18" viewBox="0 0 100 100">
                <path d={STAR_MEDIUM.path} fill={COLOR_CITED} />
              </svg>
            </span>
            <span>3-9 entries</span>
          </div>
          <div className="gv-legend-row">
            <span className="gv-legend-icon">
              <svg width="24" height="24" viewBox="0 0 100 100">
                <path d={STAR_LARGE.path} fill={COLOR_CITED} />
              </svg>
            </span>
            <span>10+ entries</span>
          </div>
        </div>

        <div className="gv-legend-divider" />

        {/* Colors */}
        <div className="gv-legend-section">
          {showTermColors ? (
            <>
              <div className="gv-legend-row">
                <span className="gv-legend-icon">
                  <span className="gv-legend-swatch" style={{ background: COLOR_TERM_A_CITED }} />
                  <span className="gv-legend-swatch" style={{ background: COLOR_TERM_A_UNCITED }} />
                </span>
                <span className="gv-legend-label">{conceptTags[0]}</span>
              </div>
              <div className="gv-legend-row">
                <span className="gv-legend-icon">
                  <span className="gv-legend-swatch" style={{ background: COLOR_TERM_B_CITED }} />
                  <span className="gv-legend-swatch" style={{ background: COLOR_TERM_B_UNCITED }} />
                </span>
                <span className="gv-legend-label">{conceptTags[1]}</span>
              </div>
              {termCount === 3 && (
                <div className="gv-legend-row">
                  <span className="gv-legend-icon">
                    <span className="gv-legend-swatch" style={{ background: COLOR_TERM_C_CITED }} />
                    <span className="gv-legend-swatch" style={{ background: COLOR_TERM_C_UNCITED }} />
                  </span>
                  <span className="gv-legend-label">{conceptTags[2]}</span>
                </div>
              )}
              <div className="gv-legend-row">
                <span className="gv-legend-icon">
                  <span className="gv-legend-swatch" style={{ background: COLOR_BLEND_CITED }} />
                  <span className="gv-legend-swatch" style={{ background: COLOR_BLEND_UNCITED }} />
                </span>
                <span>Blend</span>
              </div>
              {nebulaPath && (
                <div className="gv-legend-row">
                  <span className="gv-legend-icon">
                    <span className="gv-legend-swatch gv-legend-nebula-swatch" style={{ background: NEBULA_COLOR, opacity: 0.3 }} />
                  </span>
                  <span>Convergence</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="gv-legend-row">
                <span className="gv-legend-icon">
                  <span className="gv-legend-swatch" style={{ background: COLOR_CITED }} />
                </span>
                <span>Cited by AI</span>
              </div>
              <div className="gv-legend-row">
                <span className="gv-legend-icon">
                  <span className="gv-legend-swatch" style={{ background: COLOR_UNCITED }} />
                </span>
                <span>Analyzed</span>
              </div>
            </>
          )}
        </div>

        <div className="gv-legend-divider" />

        {/* Edge */}
        <div className="gv-legend-section">
          <div className="gv-legend-row">
            <span className="gv-legend-icon">
              <svg width="24" height="4">
                <line x1="0" y1="2" x2="24" y2="2" stroke={COLOR_EDGE} strokeWidth="2" />
              </svg>
            </span>
            <span>Shared tags</span>
          </div>
        </div>
      </div>
    );
  };

  // =========================================
  // Render
  // =========================================

  if (!nodes || nodes.length === 0) {
    return (
      <div className="gv-container gv-empty">
        <p>No graph data available for this query.</p>
      </div>
    );
  }

  const viewBoxStr = `${currentVB.x} ${currentVB.y} ${currentVB.w} ${currentVB.h}`;

  return (
    <div className="gv-container">
      <button className="gv-reset-button" onClick={handleResetView}>
        Reset view
      </button>
      <svg
        ref={svgRef}
        className={`gv-svg ${isPanning ? 'gv-grabbing' : ''}`}
        width="100%"
        viewBox={viewBoxStr}
        preserveAspectRatio="xMidYMid meet"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { handleMouseUp(); handleMouseLeave(); }}
      >
        {/* Nebula overlay — behind everything */}
        {nebulaPath && (
          <path
            className="gv-nebula"
            d={nebulaPath}
            fill={NEBULA_COLOR}
            opacity={NEBULA_OPACITY}
          />
        )}

        {/* Edges */}
        {edges.map((edge, i) => {
          const sourceNode = nodes.find(n => n.id === edge.source);
          const targetNode = nodes.find(n => n.id === edge.target);
          if (!sourceNode || !targetNode) return null;

          return (
            <line
              key={`e-${i}`}
              x1={sourceNode.x}
              y1={sourceNode.y}
              x2={targetNode.x}
              y2={targetNode.y}
              stroke={COLOR_EDGE}
              strokeWidth={edgeStrokeWidth(edge.weight)}
              opacity={edgeOpacity(edge.weight)}
              onMouseEnter={(e) => handleEdgeHover(e, edge)}
              onMouseMove={handleTooltipMove}
              onMouseLeave={handleMouseLeave}
              style={{ pointerEvents: 'stroke' }}
            />
          );
        })}

        {/* Nodes (star icons) */}
        {nodes.map((node) => {
          const star = getStarTier(node);
          const s = star.targetSize / star.size;
          return (
            <g
              key={node.id}
              transform={`translate(${node.x}, ${node.y}) scale(${s}) translate(${-star.cx}, ${-star.cy})`}
              onMouseEnter={(e) => handleNodeHover(e, node)}
              onMouseMove={handleTooltipMove}
              onMouseLeave={handleMouseLeave}
              onClick={(e) => handleNodeClick(e, node)}
              style={{ cursor: 'pointer' }}
            >
              <path
                d={star.path}
                fill={getNodeColor(node, conceptTags)}
                stroke={getNodeStroke(node)}
                strokeWidth={0.5 / s}
                fillRule={star.fillRule}
              />
            </g>
          );
        })}
      </svg>

      {renderLegend()}

      {tooltip && (
        <div
          className="gv-tooltip"
          style={{
            left: tooltip.x + 12,
            top: tooltip.y - 10
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

export default GraphVisualization;
